import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FaCircleXmark, FaBan } from "react-icons/fa6";
import { FaEye } from "react-icons/fa";
import { SubTitle } from "../../Components/CustomComponents";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import { api_urls } from "../../utilities";
import { getAgentService, getService, patchService, patchAgentService, postAgentService, postService } from "../../redux/services";
import styles from "./styles/quote_management.module.scss";



const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toTitleCase = (value = "") =>
  String(value)
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const resolveStatusMeta = (item) => {
  const raw = String(item?.status || "").trim().toLowerCase();
  if (raw.includes("cancel")) {
    return { key: "cancelled", label: "Cancelled" };
  }
  if (raw.includes("reject")) {
    return { key: "rejected", label: "Rejected" };
  }
  if (raw.includes("approve")) {
    return { key: "approved", label: "Approved" };
  }
  if (raw.includes("convert")) {
    return { key: "converted", label: "Converted" };
  }
  if (raw.includes("pending")) {
    return { key: "pending", label: "Pending" };
  }

  const quoteStatus = Number(item?.Quotation?.status);
  if (quoteStatus === 1) return { key: "approved", label: "Approved" };
  if (quoteStatus === 2) return { key: "rejected", label: "Rejected" };

  if (raw) {
    return {
      key: "pending",
      label: toTitleCase(raw),
    };
  }
  return { key: "pending", label: "Pending" };
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return "-";
  return `PKR ${amount.toLocaleString("en-PK")}`;
};

const formatBool = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1") {
    return "Yes";
  }
  if (value === false || value === "false" || value === 0 || value === "0") {
    return "No";
  }
  return "-";
};

const getServiceLabel = (item) => {
  const fromFormData = item?.form_data?.type_of_service;
  const fromTopLevel = item?.type_of_service;
  const fromArray = Array.isArray(fromFormData) ? fromFormData : fromTopLevel;
  if (Array.isArray(fromArray) && fromArray.length) {
    const labels = fromArray
      .map((entry) => entry?.title || entry?.name)
      .filter(Boolean);
    if (labels.length) return labels.join(", ");
  }
  return item?.service?.title || "-";
};

const buildRows = (source) =>
  source.map((item) => {

    console.log("[buildRows] item:", item);
    const formData = item?.form_data || {};
    const statusMeta = resolveStatusMeta(item);
    return {
      ...item,
      quote_number: `Q-${item.id}`,
      customer_name: item?.User?.name || "-",
      artist_name: item?.Artist?.name || "-",
      service_title: getServiceLabel(item),
      quote_status: statusMeta.key,
      quote_status_label: statusMeta.label,
      appointment_location:
        item?.quote_location ||
        item?.Location?.name ||
        item?.location?.name ||
        item?.User?.location?.city ||
        "-",
      estimated_rate: item?.Quotation?.estimated_rate ?? item?.estimated_rate ?? null,
      additional_charges: item?.Quotation?.additional_charges ?? null,
      tattoo_size: item?.Quotation?.tattoo_size || formData?.size || item?.tattoo_size || "-",
      quote_description:
        item?.Quotation?.description || formData?.description || item?.quote_description || "-",
      artist_experience:
        item?.Artist?.experience_years ??
        item?.Artist?.experience ??
        item?.artist?.experience_years ??
        item?.artist?.experience ??
        item?.artist_detail?.experience_years ??
        item?.artist_detail?.experience ??
        item?.artist_details?.experience_years ??
        null,
      artist_rating:
        item?.Artist?.rating ??
        item?.Artist?.average_rating ??
        item?.artist?.rating ??
        item?.artist?.average_rating ??
        item?.artist_detail?.rating ??
        item?.artist_detail?.average_rating ??
        item?.artist_details?.rating ??
        null,
      under_18: item?.under_18 ?? formData?.under_18 ?? null,
      piercing_earring: item?.piercing_earring ?? formData?.piercing_earring ?? null,
      tattoo_placement: item?.tattoo_placement || formData?.body_location || "-",
      body_class: item?.body_class || formData?.style || "-",
    };
  });

const normalizeQuoteList = (inner) => {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  return (
    inner.data ??
    inner.quote_requests ??
    inner.quoteRequests ??
    inner.quotes ??
    inner.records ??
    inner.list ??
    []
  );
};

const QuoteManagement = () => {
  const portal = useSelector((state) => state.auth?.portal);

  const [innerData, setInnerData] = useState([]);
  console.log("[QuoteManagement] innerData:", innerData);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const fetchQuoteRequests = async () => {
      setLoading(true);
      try {
        const response =
          portal === "agent"
            ? await getAgentService(api_urls.agentQuotes, {})
            : await getService(api_urls.quoteRequests, {});
        const envelope = response?.data ?? {};
        const inner = envelope?.data ?? envelope;
        const list = normalizeQuoteList(inner);
        setRowData(buildRows(Array.isArray(list) ? list : []));
      } catch (error) {
        const msg =
          error?.data?.message ??
          error?.message ??
          "Failed to load quote requests";
        Toast(msg, "error");
        setRowData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchQuoteRequests();
  }, [portal]);

  const handleViewQuote = async (row) => {
    setSelectedQuote(row);
    setPrepareLoading(true);
    try {
      const response =
        portal === "agent"
          ? await getAgentService(api_urls.agentQuotePrepare(row.id))
          : await getService(api_urls.quotePrepare(row.id));

      const envelope = response?.data ?? {};
      console.log("[QuotePrepare] raw response:", response);
      console.log("[QuotePrepare] envelope:", envelope);
      setInnerData(envelope?.data ?? envelope);

      const prepareData = envelope?.data ?? envelope;
      console.log("[QuotePrepare] usable data:", prepareData);

      if (prepareData && typeof prepareData === "object") {
        setSelectedQuote((prev) => ({ ...prev, _prepare: prepareData }));
      }
    } catch (error) {
      console.error("[QuotePrepare] error:", error);
    } finally {
      setPrepareLoading(false);
    }
  };

  const handleCancelQuote = async () => {
    if (!selectedQuote) return;
    setCancelling(true);
    try {
      portal === "agent"
        ? await postAgentService(api_urls.agentQuoteCancel(selectedQuote.id))
        : await postService(api_urls.quoteRequestCancel(selectedQuote.id));

      setRowData((prev) =>
        prev.map((q) =>
          q.id === selectedQuote.id
            ? { ...q, quote_status: "cancelled", quote_status_label: "Cancelled" }
            : q
        )
      );
      setSelectedQuote((prev) => ({
        ...prev,
        quote_status: "cancelled",
        quote_status_label: "Cancelled",
      }));
      Toast("Quote cancelled successfully", "success");
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Failed to cancel quote";
      Toast(msg, "error");
    } finally {
      setCancelling(false);
    }
  };

  const isClosed = ["cancelled", "rejected", "converted"].includes(
    selectedQuote?.quote_status
  );

  const columns = [
    {
      dataField: "quote_number",
      text: "Quote #",
      sort: true,
      headerStyle: { width: "120px" },
    },
    {
      dataField: "customer_name",
      text: "Customer",
      sort: true,
      headerStyle: { width: "220px" },
    },
    {
      dataField: "artist_name",
      text: "Artist",
      sort: true,
      headerStyle: { width: "220px" },
    },
    {
      dataField: "service_title",
      text: "Service",
      sort: true,
      headerStyle: { width: "280px" },
    },
    {
      dataField: "quote_status",
      text: "Status",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "190px" },
      formatter: (cell, row) => {
        const key = (cell || "pending").toLowerCase();
        return (
          <span className={`${styles.statusBadge} ${styles[key] || styles.pending}`}>
            {row?.quote_status_label || toTitleCase(key)}
          </span>
        );
      },
    },
    {
      dataField: "createdAt",
      text: "Quote Date",
      sort: true,
      headerStyle: { width: "240px" },
      formatter: (cell) => formatDate(cell),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "170px" },
      formatter: (cellContent, row) => (
        <button
          type="button"
          className={styles.actionBtn}
          onClick={() => handleViewQuote(row)}
        >
          <FaEye />
          <span>View</span>
        </button>
      ),
    },
  ];

  const indication = () => (loading ? "loading..." : "No quotes found");

  return (
    <div className={styles.MainContainer}>
      {selectedQuote ? (
        <ModalContainer handleClose={() => { setSelectedQuote(null); setPrepareLoading(false); }}>
          <div className={styles.quoteModal}>
            <div className={styles.quoteModalHeader}>
              <div>
                <span className={styles.quoteModalTag}>
                  {selectedQuote.quote_number}
                </span>
                <p className={styles.quoteModalTitle}>Quote Details</p>
              </div>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => { setSelectedQuote(null); setPrepareLoading(false); }}
              >
                <FaCircleXmark />
              </button>
            </div>

            {/* Status strip */}
            <div className={styles.statusStrip}>
              <span
                className={`${styles.statusBadge} ${
                  styles[selectedQuote.quote_status] || styles.pending
                }`}
              >
                {selectedQuote.quote_status_label || toTitleCase(selectedQuote.quote_status)}
              </span>
              <span className={styles.quoteDate}>
                Quote Date: {formatDate(selectedQuote.createdAt)}
              </span>
            </div>

            {prepareLoading && (
              <div style={{ textAlign: "center", padding: "8px 0", color: "#aaa", fontSize: "13px" }}>
                Loading additional details...
              </div>
            )}

            <div className={styles.detailsBody}>
              <div className={styles.detailCard}>
                <h6>Quick Information</h6>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldItem}>
                    <span>Customer</span>
                    <strong>{selectedQuote.customer_name}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Artist</span>
                    <strong>{selectedQuote.artist_name}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Service</span>
                    <strong>{selectedQuote.service_title}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Appointment Location</span>
                    <strong>{selectedQuote.appointment_location}</strong>
                  </div>
                </div>
              </div>

              {/* Row 2: Charges */}
              <div className={styles.detailCard}>
                <h6>Charges &amp; Tattoo Preference</h6>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldItem}>
                    <span>Estimated Rate</span>
                    <strong>{formatCurrency(selectedQuote.estimated_rate)}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Tattoo Size</span>
                    <strong>{selectedQuote.tattoo_size}</strong>
                  </div>
                </div>
              </div>

              {/* Row 3: Artist */}
              <div className={styles.detailCard}>
                <h6>Artist Details</h6>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldItem}>
                    <span>Experience</span>
                    <strong>
                      {innerData?.artists?.[0]?.UserShop?.experience_years != null
                        ? `${innerData?.artists?.[0]?.UserShop?.experience_years} years`
                        : "-"}
                    </strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Rating</span>
                    <strong>
                      {innerData?.artists?.[0]?.UserShop?.total_rating != null
                        ? `⭐ ${innerData?.artists?.[0]?.UserShop?.total_rating} / 5`
                        : "-"}
                    </strong>
                  </div>
                </div>
              </div>

              <div className={styles.detailCard}>
                <h6>Description</h6>
                <p className={styles.descriptionText}>
                  {selectedQuote.quote_description}
                </p>
              </div>

              <div className={styles.detailCard}>
                <h6>Request Meta</h6>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldItem}>
                    <span>Under 18</span>
                    <strong>{formatBool(selectedQuote.under_18)}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Per Hour Rate</span>
                    <strong>{innerData?.artists?.[0]?.UserShop?.rate ?? "-"}$</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Tattoo Placement</span>
                    <strong>{selectedQuote.tattoo_placement || "-"}</strong>
                  </div>
                  <div className={styles.fieldItem}>
                    <span>Body Class</span>
                    <strong>{selectedQuote.body_class || "-"}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.quoteModalFooter}>
              <button
                type="button"
                className={`${styles.cancelQuoteBtn} ${
                  isClosed ? styles.cancelQuoteBtnDisabled : ""
                }`}
                onClick={handleCancelQuote}
                disabled={isClosed || cancelling}
              >
                <FaBan />
                <span>
                  {cancelling
                    ? "Cancelling..."
                    : isClosed
                    ? `Quote ${selectedQuote.quote_status.charAt(0).toUpperCase() + selectedQuote.quote_status.slice(1)}`
                    : "Cancel Quote"}
                </span>
              </button>
            </div>
          </div>
        </ModalContainer>
      ) : (
        ""
      )}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text={"Quote Management"} />
        </div>
      </div>

      <div className={styles.mainTable}>
        <Table data={rowData} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default QuoteManagement;
